import { useRouter } from 'next/router'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import useCurrentUser from '~/hooks/useCurrentUser'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'
import {
  getCountryCodeFromCountryNameAndData,
  getCountryNameFromCountryCodeAndData,
} from '~/countries/utils/countries.utils'
import { AppLanguage } from '~/types/locale'
import { getCookie } from '~/utils/cookie.utils'
import { CountryDetailsType } from '~/types/common.types'

import { getCurriculumCountryCodeWithGradePrefix } from '~/curriculum/utils/curriculumUrl.utils'

import { CurriculumCountryCodes } from '../constants/curriculum.const'
import { getUsCurriculumStateCountryCode } from '../constants/usCurriculumStates.const'
import { useGetCurriculumCountriesQuery } from '../apis/curriculum.api'

// Re-exported so existing importers keep working; the pure helper lives in utils so it can be used without the store
export { getCurriculumCountryCodeWithGradePrefix }

export default function useGetCurriculumCountryCodeForURL(specificCountryCode?: string): string {
  const userCurriculumCountryCode = useGetUserChosenCurriculumCountryCode()
  const curriculumCountryCode = specificCountryCode ?? userCurriculumCountryCode
  return getCurriculumCountryCodeWithGradePrefix(curriculumCountryCode)
}

export function useGetCurriculumCountryCodeFromUrlWithoutPrefix(): string {
  const router = useRouter()
  const countryCode = router.query?.countryCode as string
  let countryCodeWithouPrefix = countryCode?.split('-')[1]
  if (!Object.values(CurriculumCountryCodes).includes(countryCodeWithouPrefix as CurriculumCountryCodes)) {
    countryCodeWithouPrefix = CurriculumCountryCodes.DEFAULT
  }
  return countryCodeWithouPrefix
}

export function useGetIsNorwegianCurriculum(specificCountryCode?: string): boolean {
  const userChosenCurriculumCountryCode = useGetUserChosenCurriculumCountryCode()
  const countryCode = specificCountryCode ?? userChosenCurriculumCountryCode
  return countryCode === CurriculumCountryCodes.NORWAY
}

export function useGetUserChosenCurriculumCountryCode(): string {
  const user = useCurrentUser()
  const { data } = useGetUserLocationInfoFromIpQuery()
  // if user has any chosen curriculum country or country from settings that will be returned
  // if user is not logged in and chooses a different curriculum, that will be returned
  // otherwise country code based on ip location will be returned from geo-location response
  const countryLocallyStored = typeof window !== 'undefined' ? getCookie('curriculumCountry') : undefined
  return user?.curriculumCountryCode ?? countryLocallyStored ?? data?.curriculumCountryCode?.toLowerCase() ?? '' // note: should return undefined if no country code is found
}
// if user country code is usa then we should show US onboarding else regular onboarding
export function useGetIsUsaOnboarding(): boolean {
  const { data } = useGetUserLocationInfoFromIpQuery()
  const onboardingABTest = useFeatureIsOn('us-onboarding')

  return data?.countryCode === CurriculumCountryCodes.USA.toUpperCase() || onboardingABTest
}

export function useGetCurriculumNameFromCurriculumCountryCode(countryCode: string | undefined): string {
  const { data: countriesData } = useGetCurriculumCountriesQuery()
  return getCurriculuNameFromCurriculumCountryCodeAndData(
    countriesData,
    countryCode && countryCode.trim().length > 0 ? countryCode : CurriculumCountryCodes.DEFAULT,
  )
}

export function useGetCurriculumCountryCodeFromCurriculumName(countryName: string | undefined): CurriculumCountryCodes {
  const { data: countriesData } = useGetCurriculumCountriesQuery()
  // The backend only knows its own 7 curriculum names, so it cannot resolve a Common Core state like
  // 'Ohio' — those map to 'us' from the frontend-owned state list instead.
  return (getUsCurriculumStateCountryCode(countryName) ||
    getCountryCodeFromCountryNameAndData(countriesData, countryName) ||
    CurriculumCountryCodes.DEFAULT) as CurriculumCountryCodes
}

export function getCurriculuNameFromCurriculumCountryCodeAndData(
  curriculumData: CountryDetailsType[] | undefined,
  curriculumCountryCode: string,
): string {
  return getCountryNameFromCountryCodeAndData(curriculumData, curriculumCountryCode)
}

export function useGetShowNorwegianCurriculumLanguageText(): boolean {
  const curriculumCountryCode = useGetCurriculumCountryCodeFromUrlWithoutPrefix()
  const isNorwegianCurriculum = useGetIsNorwegianCurriculum(curriculumCountryCode)
  const router = useRouter()
  return isNorwegianCurriculum && router?.locale !== AppLanguage.NO
}
